import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { Box, Card, Divider, FormControl, InputLabel, Select, MenuItem, Typography } from "@mui/material";
import { format, parse } from "date-fns";
import { useMutation, useQuery } from "@apollo/client";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import {
  GET_BUSINESS_BOOKING_TYPES,
  GET_INVITEE_ANSWERS,
  GET_INVITEE_QUESTIONS,
  GET_STAFF_BY_URL,
} from "../../../graphql/queries";
import { ADD_BOOKINGS, SEND_EMAIL_MUTATION } from "../../../graphql/mutations";
import { getBookingType, getValueFromKey, MeetingTypes } from "../../../util/meetingTypes";
import Error from "../../../components/common/error";
import BookingHeader, { getFormattedTimeRange } from "./bookingHeader";
import BookingForm from "./bookingForm";
import PaymentSection from "./paymentSection";
import { BookingStatus, validateEmail } from "../../../util/utils";
import { UserContext } from "../../../components/auth/auth-context";

/**
 * Sends a confirmation email using the provided sendEmail mutation function.
 *
 * @param {Object} params - Parameters required for sending the email.
 */
export function sendConfirmationEmail({
  time,
  bookingTypedata,
  storedTimeZone,
  staffData,
  email,
  name,
  businessUrl,
  staffUrl,
  sendEmail,
  navigate,
  res,
  providerUser
}) { 
  const parsedTimeLocal = parse(time, "yyyy-MM-dd'T'h:mma", new Date());
  const utcDate = zonedTimeToUtc(parsedTimeLocal, storedTimeZone);
  const zonedDate = utcToZonedTime(utcDate, storedTimeZone);
  const formattedDate = format(zonedDate, "iiii, MMMM do, yyyy", { timeZone: storedTimeZone });
  const formattedTime = format(zonedDate, "hh:mm a", { timeZone: storedTimeZone });
  const [timePart, ampm] = formattedTime.split(" ");
  const formattedInfoString = `${formattedDate} at <strong>${timePart} ${ampm}</strong> [${storedTimeZone}]`;

  const meetingTitle = bookingTypedata.title;
  const meetingDescription = bookingTypedata.desc;
  const locationTypeValue = getValueFromKey(bookingTypedata.meetingLocationTypeId);
  const bookingType = getBookingType(bookingTypedata.bookingTypeCategoryId);
  const durationInMinutes = bookingTypedata.length;
  const timeRange = getFormattedTimeRange(time, bookingTypedata.length);
  const formattedDuration =
    durationInMinutes >= 60
      ? `${Math.floor(durationInMinutes / 60)} hour${Math.floor(durationInMinutes / 60) > 1 ? "s" : ""}`
      : `${durationInMinutes} minute${durationInMinutes > 1 ? "s" : ""}`;

  const locationText =
    bookingTypedata.meetingLocationTypeId !== MeetingTypes.NO_LCOATION.key
      ? `${locationTypeValue} `
      : "";
  const type = bookingType.toLowerCase();
  const location = locationText.toLowerCase();
  const meetingDetailsText =
    type === "event"
      ? `Your ${formattedDuration} ${location} ${type} is confirmed!`
      : `Your ${formattedDuration} ${location} ${type} appointment is confirmed!`;

  const googleMapsLink = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    bookingTypedata.location
  )}`;
  const textLocation =
    bookingTypedata.meetingLocationTypeId === MeetingTypes.IN_PERSON.key
      ? `📍 Location: <a href="${googleMapsLink}" target="_blank" rel="noopener noreferrer">${bookingTypedata.location}</a>`
      : bookingTypedata.meetingLocationTypeId === MeetingTypes.VIDEO_MEETING.key
        ? `🔗 Meeting Link: ${bookingTypedata.location}`
        : "";
  const textContact =
    bookingTypedata.meetingLocationTypeId !== MeetingTypes.IN_PERSON.key &&
      bookingTypedata.meetingLocationTypeId !== MeetingTypes.VIDEO_MEETING.key
      ? bookingTypedata.canInviteeCall
        ? `📞 You can reach us at: <a href="tel:${bookingTypedata.phoneNumber}">${bookingTypedata.phoneNumber}</a>`
        : `📲 We will call you at the provided number.`
      : "";
      const absoluteScheduleUrl = `${window.location.origin}/myschedule`;

  // Cancellation instruction to tell user to go to /myschedule if they want to cancel
  const cancellationInstructionText = "\n\nIf you wish to cancel your booking, please visit your My Schedule page at /myschedule.";
  const cancellationInstructionHTML = `<p style="font-style: italic;">If you wish to cancel your booking, please visit your <a href="${absoluteScheduleUrl}">My Schedule</a> page.</p>`;

  const emailData = {
    from: staffData?.staffByUrl?.user?.email,
    to: email || providerUser?.email,
    subject: `🗓️  ${bookingType} Confirmation - Plany365`,
    text: `Hi ${name || providerUser?.userName},

${meetingDetailsText}

${meetingTitle}
${meetingDescription}
👤 Host: ${staffData?.staffByUrl?.user?.username}
🕒 Date & Time: ${formattedInfoString}
${textLocation ? "\n" + textLocation : ""}${textContact ? "\n" + textContact : ""}${cancellationInstructionText}`,
    html: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: auto; padding: 20px; border: 1px solid #ddd; border-radius: 8px; background-color: #f9f9f9;">
        <h2 style="color: #0073e6; text-align: center;">${bookingType} Confirmation</h2>
        <p style="text-align: center; font-size: 16px; color: #555;">You're all set for your ${bookingType.toLowerCase()}.</p>
        <div style="background: white; padding: 15px; border-radius: 6px; box-shadow: 0px 2px 5px rgba(0,0,0,0.1);">
          <p>Hi ${name},</p>
          <p>${meetingDetailsText}</p>
          <h3><strong>${meetingTitle}</strong></h3>
          <p>${meetingDescription}</p>
          <p><strong>👤 Host:</strong> ${staffData?.staffByUrl?.user?.username}</p>
          <p><strong>🕒 Date & Time:</strong> ${formattedInfoString}</p>
          ${textLocation ? `<p>${textLocation}</p>` : ""}
          ${textContact ? `<p>${textContact}</p>` : ""}
          ${cancellationInstructionHTML}
        </div>
      </div>
    `,
  };
  const bookingUrl = `/${businessUrl}/booking/servicesConfirmed/${meetingTitle}/${timeRange}/${res.data.addBooking.time}/${encodeURIComponent(bookingTypedata.color)}/${staffUrl}/${staffData?.staffByUrl?.user?.username}`;

  if (!email && !providerUser?.email) {
    // TODO: if text enabled text them
    navigate(bookingUrl);;
    return
  }

  return sendEmail({ variables: { ...emailData } })
    .then(() => {
      navigate(bookingUrl);;
    })
    .catch((error) => {
      console.error("Email sending error:", error);
      throw error;
    });
}


function BookingPage(props) {
  const { providerUser } = useContext(UserContext);
  const [answers, setAnswers] = useState({});
  const [addBooking] = useMutation(ADD_BOOKINGS);
  const [sendEmail] = useMutation(SEND_EMAIL_MUTATION);
  const [selectedOption, setSelectedOption] = useState("payNow");
  const [errors, setErrors] = useState({});
  const [hasError, setHasError] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [reminderMinutes, setReminderMinutes] = useState(15);
  // NEW STATE FOR BOOKING ERROR
  const [bookingError, setBookingError] = useState(null);

  const { bookingTypeLink, staffUrl, businessUrl, time } = useParams();
  const navigate = useNavigate();
  const storedDetails = JSON.parse(localStorage.getItem("bookingDetails"));
  const storedTimeZone = localStorage.getItem("timezone");

  const parsedTime = parse(time, "yyyy-MM-dd'T'h:mma", new Date());
  const parentPath = `/${businessUrl}/${bookingTypeLink}`;

  useEffect(() => {
    const isValidBooking =
      storedDetails &&
      storedDetails.businessUrl === businessUrl &&
      storedDetails.bookingTypeLink === bookingTypeLink &&
      storedDetails.staffUrl === staffUrl &&
      storedDetails.time === time;
    if (!isValidBooking) {
      navigate(parentPath, { replace: true });
    }
    if (!storedTimeZone) {
      navigate(parentPath, { replace: true });
    }
    const currentTime = utcToZonedTime(new Date(), storedTimeZone);
    if (parsedTime <= currentTime) {
      navigate(parentPath, { replace: true });
    }
  }, [businessUrl, bookingTypeLink, staffUrl, time, parsedTime, storedTimeZone, navigate, storedDetails]);

  useEffect(() => {
    if (providerUser && providerUser.email) {
      setEmail(providerUser.email);
    }
  }, [providerUser]);

  const { loading: staffLoading, error: staffError, data: staffData } = useQuery(GET_STAFF_BY_URL, {
    variables: { staffUrl, businessUrl },
  });
  const { loading: businessBookingTypeLoading, error: businessBookingTypeError, data: businessBookingTypes } = useQuery(
    GET_BUSINESS_BOOKING_TYPES,
    { variables: { businessUrl } }
  );
  const bookingTypedata = businessBookingTypes?.getBusinessBookingTypes?.find((obj) => obj.link === bookingTypeLink);
  const skipOtherQueries = bookingTypedata?.id ? false : true;
  const { loading: questionLoading, error: questionError, data: questionData } = useQuery(GET_INVITEE_QUESTIONS, {
    variables: { bookingTypeId: parseInt(bookingTypedata?.id) },
    skip: skipOtherQueries,
  });
  const { loading: answerLoading, error: answerError, data: answerData } = useQuery(GET_INVITEE_ANSWERS, {
    variables: { bookingTypeId: parseInt(bookingTypedata?.id) },
    skip: skipOtherQueries,
  });

  const validateQuestion = (questionId, answerValue) => {
    const question = questionData.getInviteeQuestions.find((q) => q.id === questionId);
    const emailAnswer = Object.values(answers).find((_, qId) =>
      questionData.getInviteeQuestions[qId]?.title.toLowerCase() === "email"
    );
    const phoneAnswer = Object.values(answers).find((_, qId) =>
      questionData.getInviteeQuestions[qId]?.title.toLowerCase() === "phone number"
    );

    const isEmailQ = question.title.toLowerCase() === "email";
    const isPhoneQ = question.title.toLowerCase() === "phone number";

    const hasEmail = emailAnswer && emailAnswer[0]?.trim();
    const hasPhone = phoneAnswer && phoneAnswer[0]?.trim();

    // If it's email or phone, then we allow skipping one of them if the other is filled
    if ((isEmailQ || isPhoneQ) && (hasEmail || hasPhone)) {
      return false;
    }

    // If it's a required field, validate as usual
    if (question.required && (!answerValue || answerValue.length === 0 || answerValue[0].trim() === "")) {
      return true;
    }

    // Validate email format if email is entered
    if (isEmailQ && answerValue[0]?.trim()) {
      return !validateEmail(answerValue[0]);
    }

    return false;
  };

  // Revalidate answers whenever questions or answers change
  useEffect(() => {
    if (questionData?.getInviteeQuestions) {
      const updatedErrors = {};
      questionData.getInviteeQuestions.forEach((question) => {
        const ans = answers[question.id] !== undefined ? answers[question.id] : [""];
        updatedErrors[question.id] = validateQuestion(question.id, ans);
      });
      setErrors(updatedErrors);
    }
  }, [questionData, answers]);


  useEffect(() => {
    if (questionData?.getInviteeQuestions && providerUser) {
      const defaults = {};
      questionData.getInviteeQuestions.forEach((question) => {
        if (!answers[question.id] || answers[question.id][0].trim() === "") {
          if (question.title.toLowerCase() === "email" && providerUser.email) {
            defaults[question.id] = [providerUser.email];
          }
          if (question.title.toLowerCase() === "name" && providerUser.username) {
            defaults[question.id] = [providerUser.username];
          }
        }
      });
      if (Object.keys(defaults).length > 0) {
        setAnswers((prev) => ({ ...prev, ...defaults }));
      }
    }
  }, [questionData, providerUser]);


  useEffect(() => {
    setHasError(Object.values(errors).some((error) => error));
  }, [errors]);

  useEffect(() => {
    const savedAnswers = localStorage.getItem("bookingAnswers");
    const savedBookingEmail = localStorage.getItem("bookingEmail");
    if (savedAnswers) {
      setAnswers(JSON.parse(savedAnswers));
    }
    if (savedBookingEmail) {
      setEmail(JSON.parse(savedBookingEmail));
    }
  }, []);

  // Persist answers and bookingEmail to localStorage whenever they change.
  useEffect(() => {
    localStorage.setItem("bookingAnswers", JSON.stringify(answers));
    localStorage.setItem("bookingEmail", JSON.stringify(email));
  }, [answers, email]);


  if (staffLoading || businessBookingTypeLoading || questionLoading || answerLoading) return "Loading";
  if (staffError || businessBookingTypeError || questionError || answerError) return <Error />;

  const handleAnswerChange = (questionId, value, isCheckbox = false) => {
    setAnswers((prev) => {
      let newAnswer;
      if (isCheckbox) {
        const current = prev[questionId] || [];
        newAnswer = current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value];
      } else {
        newAnswer = [value];
      }
      const question = questionData.getInviteeQuestions.find((q) => q.id === questionId);
      if (question && question.title.toLowerCase() === "email") {
        setEmail(value);
      }
      if (question && question.title.toLowerCase() === "name") {
        setName(value);
      }
      return { ...prev, [questionId]: newAnswer };
    });
  };

  const handlePaymentOptionChange = (event) => {
    setSelectedOption(event.target.value);
  };

  const handleReminderChange = (event) => {
    setReminderMinutes(Number(event.target.value));
  };

  // onConfirm callback
  const handleConfirmation = (answersRef, emailRef) => {
    const localAnswers = answersRef || answers || {};
    if (Object.keys(localAnswers).length === 0 && !emailRef) {
      console.warn("No answers provided; skipping booking.");
      return;
    }
    // Reset any previous booking error before attempting booking
    setBookingError(null);

    addBooking({
      variables: {
        input: {
          bookingTypeId: parseInt(bookingTypedata.id),
          timezone: storedTimeZone,
          bookingId: uuidv4(),
          time: zonedTimeToUtc(parsedTime, storedTimeZone).toISOString(),
          status: bookingTypedata.bookingApproval ? BookingStatus.PENDING : BookingStatus.APPROVED,
          answers: Object.keys(localAnswers).map(key => ({
            questionId: parseInt(key, 10),
            answer: localAnswers[key][0],
          })),
          staffUrl,
          businessUrl,
          finalPrice:
            selectedOption === "payNow"
              ? bookingTypedata.price - bookingTypedata.price * (bookingTypedata.discount / 100)
              : 0,
          reminderMinutes: reminderMinutes,
        },
      },
    })
      .then((res) => {
        sendConfirmationEmail({
          time,
          bookingTypedata,
          storedTimeZone,
          staffData,
          email: emailRef ?? email,
          name,
          businessUrl,
          staffUrl,
          sendEmail,
          navigate,
          res,
          providerUser
        });
      })
      .catch((err) => {
        console.error("Booking creation error:", err);
        // Update state to display error message to user
        setBookingError(err.message);
      });
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", marginTop: "3rem" }}>
      <Card
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          width: { xs: "100%", md: "80%" },
          height: "auto",
        }}
      >
        <BookingHeader
          bookingData={bookingTypedata}
          parsedTime={parsedTime}
          staff={bookingTypedata.assignedStaffs.find((val) => val.url === staffUrl)}
          storedTimeZone={storedTimeZone}
          navigate={navigate}
        />
        <Divider orientation="horizontal" sx={{ margin: "10px 0" }} />
        <BookingForm
          questions={questionData.getInviteeQuestions}
          answers={answers}
          errors={errors}
          onAnswerChange={handleAnswerChange}
          answerData={answerData}
          staff={bookingTypedata.assignedStaffs.find((val) => val.url === staffUrl)}
        />
        <Box sx={{ padding: "30px", paddingRight: "20px" }}>
          <FormControl fullWidth sx={{ marginBottom: "1rem" }}>
            <InputLabel id="reminder-select-label">Reminder Time</InputLabel>
            <Select
              labelId="reminder-select-label"
              id="reminder-select"
              value={reminderMinutes}
              label="Reminder Time"
              onChange={handleReminderChange}
            >
              <MenuItem value={5}>5 minutes</MenuItem>
              <MenuItem value={10}>10 minutes</MenuItem>
              <MenuItem value={15}>15 minutes</MenuItem>
              <MenuItem value={30}>30 minutes</MenuItem>
              <MenuItem value={60}>1 hour</MenuItem>
              <MenuItem value={120}>2 hours</MenuItem>
              <MenuItem value={1440}>1 day</MenuItem>
            </Select>
          </FormControl>
          <PaymentSection
            selectedOption={selectedOption}
            onOptionChange={handlePaymentOptionChange}
            bookingTypedata={{ ...bookingTypedata }}
            hasError={hasError}
            onConfirm={handleConfirmation}
            email={email}
            answers={answers}
          />
          {/* Display error message if bookingError state is set */}
          {bookingError && (
            <Typography variant="body2" color="error" align="center" sx={{ marginTop: "1rem" }}>
              {bookingError}
            </Typography>
          )}
        </Box>
      </Card>
    </Box>
  );
}

export default BookingPage;
